import { PkButton, PkIcon } from '@uefadigital/panenka-react';
import clsx from 'clsx';
import type { MouseEventHandler, MouseEvent } from 'react';
import { Suspense, lazy, memo, useCallback, useMemo } from 'react';

import type { RelevancyCardProps } from '../../DynamicCardComponent';
import { useCardFormatCheck } from '../../hooks/useCardFormatCheck';
import { useRoofline } from '../../hooks/useRoofline';
import { useRelevancyOptions } from '../../store';
import { RelevancyCardFormat } from '../../types';
import { isNewsRecent } from '../../utils';

import AssociationBadge from './AssociationBadge';
import CardLogo from './CardLogo';

import type { MediaImage } from '@cobalt/utilities';
import LiveIndicator from '@shared/LiveIndicator';
import PictureHelper from '@shared/PictureHelper';
import PkCardWithSponsor from '@shared/PkCardWithSponsor';
import { useTranslateFn } from '@shared/Translate';
import VideoPlayer from '@shared/VideoPlayer';
import type { PkTheme } from '@shared-types/common.types';
import { dateToString } from '@utilities/dateTime';

import * as styles from './styles.module.scss';

const Roofline = lazy(() => import('./Roofline'));

const defaultFallbackImg = `${window.imgBaseUrl}/imgml/void.svg`;

export type BaseCardProps = RelevancyCardProps & {
  url?: string;
  target?: string;
  cardContent?: 'default' | 'over';
  sponsorCode?: string;
  sponsorPosition?: 'default' | 'top-left' | 'top-right';
  isDocument?: boolean;
  isVideoCard?: boolean;
  isVideoPlayer?: boolean;
  video?: any;
  videoDuration?: number | string;
  hasLiveBadge?: boolean;
  logoImg?: string;
  logoTxtFallback?: string;
  title?: string;
  newsDate?: string;
  buttonTag?: string;
  image?: MediaImage['image'];
  fallbackImg?: string;
  theme?: PkTheme;
  association?: string;
  showRoofline?: boolean;
  iconName?: string;
  onClick?: MouseEventHandler<HTMLElement>;
  disableLink?: boolean;
};

const BaseCardComponent = ({
  item,
  cardFormat,
  cardContent,
  url,
  target = '_self',
  sponsorCode,
  sponsorPosition = 'top-left',
  isDocument,
  isVideoCard,
  isVideoPlayer,
  video,
  videoDuration,
  hasLiveBadge,
  logoImg,
  logoTxtFallback,
  title,
  newsDate,
  buttonTag,
  image,
  theme,
  fallbackImg = defaultFallbackImg,
  association,
  showRoofline,
  iconName,
  onClick,
  disableLink,
}: BaseCardProps) => {
  const t = useTranslateFn();
  const { options } = useRelevancyOptions();
  const { isWide, isPortrait, isSquareOver, isSquareUnder, imgFormat, aspectRatio } = useCardFormatCheck(cardFormat);
  const isNewCard = useMemo(() => isNewsRecent(newsDate), [newsDate]);
  const { design } = options;

  theme = theme ?? (isWide || isPortrait || isSquareOver ? 'dark' : null);

  const roofline = useRoofline(showRoofline ? item?.teaser : null);

  const isRooflineTop = roofline && options.rooflineTop;
  const isRooflineBottom = roofline && !options.rooflineTop;

  const flexColumn = isRooflineBottom || !isWide;

  const bodyClassNames = clsx({
    'pk-d--flex': true,
    [styles.body]: true,
    'pk-flex--column': true,
  });

  const bodyContentClassNames = clsx({
    [styles.bodyContent]: true,
    'pk-d--flex': true,
    'pk-flex--grow-1': true,
    'pk-justify-content--start': roofline,
    'pk-justify-content--between': !roofline,
    'pk-align-items--center': logoImg,
    'pk-flex--row': !flexColumn,
    'pk-flex--column': flexColumn,
  });

  const handleOnClick = useCallback(
    (e: MouseEvent<HTMLElement>) => {
      onClick && onClick(e);
    },
    [onClick],
  );

  const Tag = useMemo(() => {
    if (!disableLink && url) return 'a';
    return onClick ? 'button' : 'div';
  }, [disableLink, onClick, url]);

  return (
    <Tag className={styles.container} href={url} target={target} onClick={handleOnClick}>
      <PkCardWithSponsor
        role='article'
        className={clsx({
          [`pk-theme--${theme}`]: theme,
          [styles.pkCard]: true,
          [styles[`pk-card--${cardFormat}`]]: true,
          [styles.pkCardWithBtn]: !!buttonTag,
          [styles[`pk-card--${design}`]]: design && design !== 'default',
        })}
        content={cardContent ?? (isWide || isPortrait || isSquareOver ? 'over' : 'default')}
        sponsorName={sponsorCode}
        sponsorPosition={sponsorPosition}
        elevation={false}
        stroke={isWide || isPortrait || isSquareOver}
        transparency={isSquareUnder}
      >
        <div className={styles.header} slot='heading'>
          {isRooflineTop && (
            <Suspense>
              <Roofline roofline={roofline} theme={theme} />
            </Suspense>
          )}

          {isVideoPlayer && video && (
            <VideoPlayer
              controls={design === 'mosaic-single'}
              showTitle={false}
              enablePrerolls={false}
              isMuted
              repeat
              isAutoPlay='viewable'
              checkInteraction={false}
              watchOtherVideos={false}
              preload='auto'
              stretching='fill'
              aspectratio={aspectRatio}
              teaser={video}
            />
          )}
          {hasLiveBadge && <LiveIndicator className={styles.live} size='S' label='on_air_now' />}
          {isVideoCard && (
            <>
              <div className={styles.video}>
                {!!videoDuration && <span className={styles.duration}>{videoDuration}</span>}
                <PkIcon name='av-play-circle' color='interaction-high-contrast' size={38} />
              </div>
              <div className={styles.videobig}>
                <PkIcon name='av-play' color='interaction-high-contrast' size={120} />
              </div>
            </>
          )}
          {(image || !!fallbackImg) && !isVideoPlayer && (
            <PictureHelper
              formats={imgFormat}
              className={styles.img}
              image={image}
              imageUrl={fallbackImg}
              fallback={fallbackImg}
              title={title}
            />
          )}
        </div>
        <div slot='body' className={bodyClassNames}>
          {!!iconName && <PkIcon name={iconName} color='text-04' />}
          {!!association && <AssociationBadge association={association} isWide={isWide} />}
          <div className={bodyContentClassNames}>
            {isDocument && isNewCard && <div className={styles.chip}>{t('new')}</div>}
            {isRooflineBottom && (
              <Suspense>
                <Roofline roofline={roofline} theme={theme} />
              </Suspense>
            )}
            {!!title && (
              <div role='heading' aria-level={2} className={styles.title}>
                {title}
              </div>
            )}
            {isDocument && !!newsDate && (
              <div role='heading' aria-level={2} className={styles.title}>
                {dateToString(newsDate, 'MMMM YYYY')}
              </div>
            )}
            {!isWide && <div />}
            {!!logoImg && <CardLogo logoImg={logoImg} logoTxtFallback={logoTxtFallback} />}
            {!!buttonTag && (
              <PkButton width='auto' highContrast={isWide} className={styles.pkCardBtn} appearance='outlined'>
                {t(buttonTag, { abbr: cardFormat === RelevancyCardFormat.WIDE })}
              </PkButton>
            )}
          </div>
        </div>
      </PkCardWithSponsor>
    </Tag>
  );
};

export default memo(BaseCardComponent);
